import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DatabaseBackup, Download, Loader2, Play, RefreshCw, RotateCcw, Save, Trash2, Clock, HardDriveDownload, HardDrive, AlertTriangle, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function fmtBytes(n) {
  if (!n) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0; let v = Number(n);
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(i ? 1 : 0)} ${u[i]}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const hourLabel = (h) => {
  const ampm = h < 12 ? "AM" : "PM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:00 ${ampm}`;
};

export default function DevAdminBackups() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [settings, setSettings] = useState(null);
  const [backups, setBackups] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [running, setRunning] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [health, setHealth] = useState(null);
  const [storage, setStorage] = useState(null);
  const [drilling, setDrilling] = useState(null);   // backup id being drilled
  const [drillResult, setDrillResult] = useState(null);

  useEffect(() => {
    if (user && !user.is_dev_admin) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, b] = await Promise.all([
        api.get("/devadmin/backups/settings"),
        api.get("/devadmin/backups"),
      ]);
      setSettings(s.data);
      setBackups(b.data.backups || []);
      setCollections(b.data.collections || []);
      api.get("/devadmin/backups/health").then(({ data }) => setHealth(data)).catch(() => {});
      api.get("/devadmin/backups/storage").then(({ data }) => setStorage(data)).catch(() => {});
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load backups");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/devadmin/backups/settings", settings);
      setSettings(data);
      toast.success("Schedule saved");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save");
    } finally { setSavingSettings(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/devadmin/backups/run");
      toast.success("Backup started — it runs in the background and may take a few minutes.");
      await load();
      pollUntilDone();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not start backup");
      setRunning(false);
    }
  };

  // Poll the backups list until no run is in progress, then report the outcome.
  const pollUntilDone = async () => {
    for (let i = 0; i < 220; i++) {            // ~15 min at 4s intervals
      await new Promise((r) => setTimeout(r, 4000));
      let list = [];
      try {
        const { data } = await api.get("/devadmin/backups");
        list = data.backups || [];
        setBackups(list);
        setCollections(data.collections || []);
        api.get("/devadmin/backups/health").then(({ data }) => setHealth(data)).catch(() => {});
      } catch { continue; }
      if (!list.some((b) => b.status === "running")) {
        const latest = list.find((b) => b.triggered_by === "manual") || list[0];
        if (latest?.status === "completed") toast.success("Backup completed successfully");
        else if (latest?.status === "failed") toast.error(latest.error || "Backup failed");
        break;
      }
    }
    setRunning(false);
  };

  const download = async (b) => {
    try {
      // Step 1: fetch a short-lived signed download ticket (auth via Bearer).
      // Step 2: hand the URL (with ?token=) to the browser via <a href> so it
      // streams the archive to disk instead of buffering GBs in JS blob memory
      // (which silently OOMs on 5–8 GB PCs for the 5.5 GB nightly).
      const { data } = await api.get(`/devadmin/backups/${b.id}/download-ticket`);
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const url = `${base}/api/devadmin/backups/${b.id}/download?token=${encodeURIComponent(data.token)}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename || b.filename;
      a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Download starting — check your browser downloads.");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      toast.error(`Download failed: ${detail}`);
    }
  };

  const doRestore = async () => {
    setRestoring(true);
    try {
      const { data } = await api.post(`/devadmin/backups/${restoreTarget.id}/restore`, { confirm: true });
      toast.success(`Database restored from ${data.restored_from}`);
      setRestoreTarget(null); setRestoreConfirm("");
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Restore failed");
    } finally { setRestoring(false); }
  };

  const doDrill = async (b) => {
    setDrilling(b.id);
    try {
      const { data } = await api.post(`/devadmin/backups/${b.id}/drill`);
      setDrillResult(data);
      toast.success(`Recovery verified — ${data.total_docs.toLocaleString()} records across ${data.collection_count} collections`);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Restore drill failed");
    } finally { setDrilling(null); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/devadmin/backups/${deleteTarget.id}`);
      toast.success("Backup removed");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleExcluded = (name) => {
    setSettings((s) => {
      const cur = new Set(s.excluded_collections || []);
      if (cur.has(name)) cur.delete(name); else cur.add(name);
      return { ...s, excluded_collections: [...cur] };
    });
  };

  const restoreWord = "RESTORE";

  if (loading || !settings) {
    return <Layout><div className="py-24 text-center text-slate-400"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl" data-testid="devadmin-backups-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <DatabaseBackup className="h-7 w-7 text-cyan-500" /> Database Backups
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Automatic nightly backups to secure cloud storage. Download or restore the whole database anytime.
            </p>
          </div>
          <Button onClick={runNow} disabled={running} className="rounded-full bg-cyan-600 hover:bg-cyan-700" data-testid="backup-run-now">
            {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}
            {running ? "Backing up…" : "Back up now"}
          </Button>
        </div>

        {/* Health banner — surfaces failures / stale backups so they're never silent */}
        {health && (
          <div
            className={`rounded-2xl border p-4 flex items-start gap-3 ${health.healthy
              ? "border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/30"
              : "border-rose-300 bg-rose-50 dark:border-rose-900/50 dark:bg-rose-950/30"}`}
            data-testid="backup-health-banner"
          >
            {health.healthy
              ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
              : <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />}
            <div className="min-w-0">
              <div className={`text-sm font-semibold ${health.healthy ? "text-emerald-800 dark:text-emerald-300" : "text-rose-800 dark:text-rose-300"}`}>
                {health.healthy ? "Backups are healthy" : "Backup needs attention"}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 break-words">
                {health.message}
                {health.last_success_at && (
                  <> · Last successful backup: {fmtDate(health.last_success_at)}</>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Object-storage quota banner — the store has NO delete API, so a full
            quota blocks every backup until Emergent support frees space. */}
        {storage && (storage.quota_exceeded || storage.usage) && (
          <div
            className={`rounded-2xl border p-4 flex items-start gap-3 ${storage.quota_exceeded
              ? "border-amber-300 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30"
              : "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60"}`}
            data-testid="backup-storage-banner"
          >
            <HardDrive className={`h-5 w-5 shrink-0 mt-0.5 ${storage.quota_exceeded ? "text-amber-600" : "text-slate-400"}`} />
            <div className="min-w-0 space-y-1">
              <div className={`text-sm font-semibold ${storage.quota_exceeded ? "text-amber-900 dark:text-amber-200" : "text-slate-700 dark:text-slate-200"}`} data-testid="backup-storage-title">
                {storage.quota_exceeded ? "Cloud storage quota exceeded — backups cannot upload" : "Cloud storage usage"}
              </div>
              {storage.usage && (
                <div className="text-xs text-slate-600 dark:text-slate-400" data-testid="backup-storage-usage">
                  {storage.usage.truncated ? "More than " : ""}{fmtBytes(storage.usage.bytes)} in {storage.usage.objects}{storage.usage.truncated ? "+" : ""} files
                  {" · "}{storage.usage.backups}{storage.usage.truncated ? "+" : ""} stored backups
                  {" · "}largest backup {fmtBytes(storage.usage.largest_backup_bytes)}
                  {storage.usage.truncated && <span className="text-slate-400"> (listing capped at 1000 files)</span>}
                </div>
              )}
              {storage.quota_exceeded && (
                <ul className="text-xs text-amber-900/90 dark:text-amber-200/90 list-disc pl-4 space-y-0.5" data-testid="backup-storage-steps">
                  <li>Old backup files can only be removed by Emergent — email <a className="underline font-medium" href={`mailto:${storage.support_email}?subject=Object%20storage%20quota%20exceeded%20(trishul-erp)&body=Please%20purge%20objects%20under%20trishul-erp%2Fbackups%2F%20(older%20nightly%20DB%20backups)%20and%2For%20raise%20our%20storage%20quota.%20Uploads%20return%20HTTP%20439%20storage_quota_exceeded.`}>{storage.support_email}</a> asking to purge <span className="font-mono">trishul-erp/backups/</span> or raise the quota.
                  </li>
                  <li>Keep <b>Include uploaded videos</b> off below — it made each backup ~10× larger (that is what filled the quota).</li>
                  <li>Nightly runs stay scheduled and will resume automatically once space is available.</li>
                </ul>
              )}
              {!storage.quota_exceeded && storage.usage && (
                <div className="text-[11px] text-slate-400">Stored files are never deleted by retention (no delete API) — keep backups small.</div>
              )}
            </div>
          </div>
        )}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-4" data-testid="backup-settings-card">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
              <Clock className="h-4 w-4 text-cyan-500" /> Automatic schedule
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">{settings.enabled ? "On" : "Off"}</span>
              <Switch checked={settings.enabled} onCheckedChange={(v) => setSettings((s) => ({ ...s, enabled: v }))} data-testid="backup-enabled-switch" />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Daily run time (IST)</label>
              <Select value={String(settings.hour)} onValueChange={(v) => setSettings((s) => ({ ...s, hour: Number(v) }))} disabled={!settings.enabled}>
                <SelectTrigger className="rounded-xl" data-testid="backup-hour-select"><SelectValue /></SelectTrigger>
                <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Keep last (retention)</label>
              <Input type="number" min={1} max={365} value={settings.retention} onChange={(e) => setSettings((s) => ({ ...s, retention: Number(e.target.value) }))} className="rounded-xl" data-testid="backup-retention-input" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Exclude collections (optional)</label>
            <div className="flex flex-wrap gap-1.5" data-testid="backup-exclude-list">
              {collections.map((c) => {
                const on = (settings.excluded_collections || []).includes(c);
                return (
                  <button key={c} type="button" onClick={() => toggleExcluded(c)}
                    className={`px-2.5 py-1 rounded-full text-xs font-mono border transition-colors ${on ? "bg-rose-50 dark:bg-rose-900/30 border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-300 line-through" : "bg-slate-50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-300 hover:border-cyan-300"}`}
                    data-testid={`backup-exclude-${c}`}>
                    {c}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">Excluded collections are left out of every backup. Click to toggle.</p>
          </div>

          <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 dark:border-slate-700 p-3" data-testid="backup-include-media-row">
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-700 dark:text-slate-200">Include uploaded videos in backups</div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Video files (GridFS <span className="font-mono">tuition_videos.*</span>) are large and never change. Including them makes each nightly backup several GB and fills cloud storage quickly. Lesson records and all other data are always backed up.
              </p>
            </div>
            <Switch checked={!!settings.include_media} onCheckedChange={(v) => setSettings((s) => ({ ...s, include_media: v }))} data-testid="backup-include-media-switch" />
          </div>

          <div className="flex justify-end">
            <Button onClick={saveSettings} disabled={savingSettings} className="rounded-full bg-slate-800 hover:bg-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-white" data-testid="backup-save-settings">
              {savingSettings ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save schedule
            </Button>
          </div>
        </div>

        {/* Backups list */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">Backups ({backups.length})</div>
            <Button variant="outline" size="sm" onClick={load} className="rounded-full" data-testid="backup-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="backup-table">
              <thead className="bg-slate-50 dark:bg-slate-900/40 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">When</th>
                  <th className="text-left px-4 py-2.5">File</th>
                  <th className="text-right px-4 py-2.5">Size</th>
                  <th className="text-left px-4 py-2.5">Trigger</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {backups.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-12 text-slate-400" data-testid="backup-empty">No backups yet. Click “Back up now” to create one.</td></tr>
                )}
                {backups.map((b) => (
                  <tr key={b.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40" data-testid={`backup-row-${b.id}`}>
                    <td className="px-4 py-3 text-slate-700 dark:text-slate-200">{fmtDate(b.created_at)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500 max-w-[220px] truncate" title={b.filename}>
                      {b.filename}
                      {b.status === "failed" && b.error && (
                        <div className="mt-1 font-sans text-[11px] leading-snug text-rose-600 dark:text-rose-400 whitespace-normal break-words" data-testid={`backup-error-${b.id}`}>
                          {b.error}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-600 dark:text-slate-300">{fmtBytes(b.size)}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300">{b.triggered_by}</span>
                    </td>
                    <td className="px-4 py-3">
                      {b.status === "completed" ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-300 text-xs font-medium"><CheckCircle2 className="h-3.5 w-3.5" /> Completed</span>
                      ) : b.status === "failed" ? (
                        <span className="inline-flex items-center gap-1 text-rose-600 text-xs font-medium" title={b.error}><XCircle className="h-3.5 w-3.5" /> Failed</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-500 text-xs font-medium"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Running</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" className="rounded-full text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30" disabled={b.status !== "completed" || drilling === b.id} onClick={() => doDrill(b)} title="Safe recovery test (restores into a throwaway copy, live data untouched)" data-testid={`backup-drill-${b.id}`}>
                        {drilling === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                      </Button>
                      <Button variant="ghost" size="sm" className="rounded-full text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-900/30" disabled={b.status !== "completed"} onClick={() => download(b)} data-testid={`backup-download-${b.id}`}><Download className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="sm" className="rounded-full text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30" disabled={b.status !== "completed"} onClick={() => { setRestoreConfirm(""); setRestoreTarget(b); }} data-testid={`backup-restore-${b.id}`}><RotateCcw className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="sm" className="rounded-full text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30" onClick={() => setDeleteTarget(b)} data-testid={`backup-delete-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Restore drill result dialog */}
        <Dialog open={!!drillResult} onOpenChange={(o) => { if (!o) setDrillResult(null); }}>
          <DialogContent className="max-w-md" data-testid="backup-drill-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2 text-emerald-600">
                <ShieldCheck className="h-5 w-5" /> Recovery verified
              </DialogTitle>
              <DialogDescription>
                Restored <span className="font-semibold">{drillResult?.total_docs?.toLocaleString()}</span> records
                across <span className="font-semibold">{drillResult?.collection_count}</span> collections from
                <span className="font-mono text-xs"> {drillResult?.backup_filename}</span> into a throwaway copy — then cleaned it up.
                <span className="block mt-1 text-emerald-600">Your live data was never touched. This backup is restorable. ✅</span>
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-56 overflow-auto rounded-lg border border-slate-200 dark:border-slate-700 text-xs">
              <table className="w-full">
                <tbody>
                  {Object.entries(drillResult?.collections || {}).sort((a, b) => b[1] - a[1]).map(([name, n]) => (
                    <tr key={name} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                      <td className="px-3 py-1.5 font-mono text-slate-600 dark:text-slate-300">{name}</td>
                      <td className="px-3 py-1.5 text-right text-slate-500">{n.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDrillResult(null)} data-testid="backup-drill-close">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Restore dialog */}
        <Dialog open={!!restoreTarget} onOpenChange={(o) => { if (!o) { setRestoreTarget(null); setRestoreConfirm(""); } }}>          <DialogContent className="max-w-md" data-testid="backup-restore-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-5 w-5" /> Restore database?
              </DialogTitle>
              <DialogDescription>
                This will <span className="font-semibold">replace ALL current data</span> with the contents of
                <span className="font-mono text-xs"> {restoreTarget?.filename}</span> ({fmtBytes(restoreTarget?.size)}). The live database will be dropped and rebuilt from this backup. This cannot be undone.
                <br />Type <span className="font-mono font-bold text-amber-600">{restoreWord}</span> to confirm.
              </DialogDescription>
            </DialogHeader>
            <Input value={restoreConfirm} onChange={(e) => setRestoreConfirm(e.target.value)} placeholder={restoreWord} className="font-mono" data-testid="backup-restore-confirm-input" />
            <DialogFooter>
              <Button variant="outline" onClick={() => { setRestoreTarget(null); setRestoreConfirm(""); }} disabled={restoring}>Cancel</Button>
              <Button className="bg-amber-500 hover:bg-amber-600" disabled={restoreConfirm !== restoreWord || restoring} onClick={doRestore} data-testid="backup-restore-confirm-btn">
                {restoring ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <HardDriveDownload className="h-4 w-4 mr-1.5" />} Restore
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete dialog */}
        <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
          <DialogContent className="max-w-md" data-testid="backup-delete-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2 text-rose-600"><Trash2 className="h-5 w-5" /> Remove this backup?</DialogTitle>
              <DialogDescription>This removes <span className="font-mono text-xs">{deleteTarget?.filename}</span> from the list. It will no longer be available to restore.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button className="bg-rose-500 hover:bg-rose-600" onClick={doDelete} data-testid="backup-delete-confirm-btn">Remove</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
